import { CARD_WIDTH } from '../design/tokens';
import type { PlanePlacement } from './planeKey';

/** What a nook card is wide, small and opened out. */
export const NOOK_WIDTH = { small: 280, open: CARD_WIDTH };
/** How small and how large a sticky can be pulled. */
export const STICKY_SIZE = { min: { w: 140, h: 80 }, max: { w: 720, h: 720 } };

/** One line in canvas pixels; a card's height on the canvas is its lines times this. */
export const LINE = 24;

/** What a card is, as far as its collapsed height goes. */
export type CardKind = 'recording' | 'topic' | 'arc' | 'note' | 'nook' | 'other';

const DEFAULT_LINES = 2.5;

/**
 * A collapsed card's height in lines when its plane line carries no `~N`, as the app estimates it
 * (`CardLines.collapsed`): an arc by its title, up to three lines of it; a note by its text, up to
 * eight lines of it plus padding, or 5 while its text is unknown; anything else 2.5. [text] is the
 * arc's title or the note's text.
 */
export function collapsedLines(kind: CardKind, text?: string | null): number {
  if (kind === 'arc') {
    const title = text?.trim() ?? '';
    const titleLines = title === '' ? 1 : Math.min(3, Math.ceil(title.length / 30));
    return DEFAULT_LINES + titleLines - 1;
  }
  if (kind === 'note') {
    if (text == null) return 5;
    const shown = text
      .trim()
      .split(/\r\n|\r|\n/)
      .reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / 36)), 0);
    return Math.min(8, Math.max(1, shown)) + 2;
  }
  return DEFAULT_LINES;
}

/** An arc's title as its path gives it: `arcs/<Title>.md`, or `arcs/<Title> (XXXXXX).md` where
 *  another unit already had the name. */
export function arcTitleOf(path: string): string {
  return path
    .slice(path.lastIndexOf('/') + 1)
    .replace(/\.md$/, '')
    .replace(/ \([0-9A-Z]{6}\)$/, '');
}

/** A sticky's height: its own `×w,h` when the line has one, else its `~N`, else [fallback]. */
export function stickyHeight(placement: Pick<PlanePlacement, 'h' | 'lines'>, fallback: number): number {
  return placement.h ?? (placement.lines !== undefined ? placement.lines * LINE : fallback);
}

/** A collapsed card's height: its `~N` when the line has one, else [collapsedLines] for its kind. */
export function cardHeight(placement: Pick<PlanePlacement, 'lines'>, kind: CardKind, text?: string | null): number {
  return (placement.lines ?? collapsedLines(kind, text)) * LINE;
}

/**
 * Calls [onSettled] with [el]'s height once it has held for [frames] frames running, or after
 * [maxMs]: a card opened before its text has loaded is still growing on the frame it opens. The
 * returned function cancels.
 */
export function whenHeightSettles(el: HTMLElement, onSettled: (height: number) => void, frames = 3, maxMs = 5000): () => void {
  const start = performance.now();
  let last = -1;
  let held = 0;
  let handle = 0;
  const tick = () => {
    const height = el.offsetHeight;
    held = height === last ? held + 1 : 0;
    last = height;
    if (held >= frames || performance.now() - start > maxMs) onSettled(height);
    else handle = requestAnimationFrame(tick);
  };
  handle = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(handle);
}
